import { useCallback } from 'react';
import { ApolloError, useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import {
  useErpCareerSkillsDictionaryQuery,
  useJoinCareerSkillsMutation,
  useSaveCareerSkillIconMutation,
  useSaveCareerSkillMutation,
  useToggleCareerSkillMutation,
} from 'core/graphql';

import {
  ICareerSkillDictionaryRow,
  ISkillLevelCriteria,
  ISkillMutationResult,
} from './careerModel';

const EMPTY: ICareerSkillDictionaryRow[] = [];

/**
 * ТЗ 0039 — справочник навыков во вкладке «Навыки».
 *
 * Отдельно от `useCareerSkills`: тот кормит мультиселект формы вакансии и
 * обязан оставаться одним дешёвым запросом без счётчиков и без неактивных
 * строк. Здесь наоборот — нужны и те, и другие.
 *
 * ⚠️ Счётчики приходят НУЛЛАБЕЛЬНЫМИ: сервер считает их только при
 * `withStats`, и `null` означает «на этом запросе не считалось», а не ноль.
 * Здесь мы запрашиваем со статистикой, поэтому приводим к числу на границе.
 */
export const useSkillDictionary = (search?: string, category?: string) => {
  const { data, loading, error, refetch } = useErpCareerSkillsDictionaryQuery({
    variables: { query: search || undefined, category: category || undefined },
    fetchPolicy: 'cache-and-network',
  });

  const rows: ICareerSkillDictionaryRow[] =
    data?.careerSkillsErp?.map((row) => ({
      id: row.id,
      name: row.name,
      slug: row.slug,
      category: row.category,
      description: row.description,
      aliases: [...(row.aliases ?? [])],
      iconUrl: row.iconUrl ?? null,
      isActive: row.isActive,
      holderCount: row.holderCount ?? 0,
      courseCount: row.courseCount ?? 0,
      // ⚠️ НЕ `?? 0`: отсутствие снимка и «ноль вакансий» — разные вещи, и
      // ноль вместо прочерка читается как «навык никому не нужен».
      demandVacancies: row.demandVacancies ?? null,
      levelCriteria: (row.levelCriteria ?? []).map((item) => ({
        level: item.level,
        points: [...item.points],
        example: item.example ?? '',
      })),
    })) ?? EMPTY;

  return { rows, loading, error: error as ApolloError | undefined, refetch };
};

/** Мутации справочника. Удаления среди них нет и не должно быть. */
export const useSkillDictionaryMutations = () => {
  const [saveSkill, saveState] = useSaveCareerSkillMutation();
  const [toggleSkill, toggleState] = useToggleCareerSkillMutation();
  const [joinSkills, joinState] = useJoinCareerSkillsMutation();
  const [saveIcon, iconState] = useSaveCareerSkillIconMutation();

  const upsert = useCallback(
    async (input: {
      id?: string;
      name: string;
      category: string;
      description?: string;
      aliases?: string[];
      isActive?: boolean;
      levelCriteria?: ISkillLevelCriteria[];
    }): Promise<ISkillMutationResult> => {
      const { data } = await saveSkill({ variables: { input } });
      const payload = data?.upsertCareerSkill;
      return {
        success: Boolean(payload?.success),
        errors: (payload?.customErrors ?? []).map((item) => ({
          field: item?.field ?? '',
          message: item?.message ?? '',
        })),
      };
    },
    [saveSkill],
  );

  const setActive = useCallback(
    async (id: string, isActive: boolean): Promise<ISkillMutationResult> => {
      const { data } = await toggleSkill({ variables: { id, isActive } });
      return {
        success: Boolean(data?.setCareerSkillActive?.success),
        errors: [],
      };
    },
    [toggleSkill],
  );

  const merge = useCallback(
    async (
      sourceId: string,
      targetId: string,
    ): Promise<ISkillMutationResult> => {
      const { data } = await joinSkills({ variables: { sourceId, targetId } });
      const payload = data?.mergeCareerSkills;
      return {
        success: Boolean(payload?.success),
        moved:
          (payload?.movedStudentSkills ?? 0) +
          (payload?.movedVacancies ?? 0) +
          (payload?.movedProjects ?? 0) +
          (payload?.movedCourseSkills ?? 0),
        errors: (payload?.customErrors ?? []).map((item) => ({
          field: item?.field ?? '',
          message: item?.message ?? '',
        })),
      };
    },
    [joinSkills],
  );

  /**
   * Своя картинка навыка либо возврат к автоподбору. Файл едет мультипартом
   * (`Upload`-скаляр, терминирующая ссылка Apollo — `createUploadLink`), тем
   * же путём, что видео урока: сервер пишет байты сам, CORS бакета не нужен.
   */
  const setIcon = useCallback(
    async (id: string, file: File | null): Promise<ISkillMutationResult> => {
      try {
        const { data } = await saveIcon({
          variables: { id, icon: file ?? undefined, clear: !file },
        });
        const payload = data?.setCareerSkillIcon;
        return {
          success: Boolean(payload?.success),
          errors: (payload?.customErrors ?? []).map((item) => ({
            field: item?.field ?? '',
            message: item?.message ?? '',
          })),
        };
      } catch (error) {
        // 🔴 Отказ приезжает ИСКЛЮЧЕНИЕМ Apollo, а не в `customErrors`:
        // при дефолтном `errorPolicy: 'none'` мутация реджектится. Без этого
        // ветвления оператор видел РОВНО НИЧЕГО — кнопка разблокировалась,
        // иконка прежняя, ошибки нет, и он жал по кругу. Сюда попадает и
        // слишком большой файл, и отказ по правам.
        return {
          success: false,
          errors: [{ field: 'icon', message: (error as Error)?.message ?? '' }],
        };
      }
    },
    [saveIcon],
  );

  return {
    upsert,
    setActive,
    merge,
    setIcon,
    saving:
      saveState.loading ||
      toggleState.loading ||
      joinState.loading ||
      iconState.loading,
  };
};

/**
 * Кто правит справочники карьерного домена: ADMIN и METHODIST.
 *
 * Зеркалит серверный `CAREER_DICTIONARY_WRITE_ROLES`. Живёт здесь, а не в
 * компоненте: с ТЗ 0040 тот же вопрос задаёт карточка курса, а две копии
 * ролевого списка разъезжаются молча — и расхождение видно только по кнопке,
 * которая всегда отбивается.
 *
 * Проверка НЕ заменяет серверную, а убирает кнопку, которая для этой роли
 * гарантированно закончится ошибкой: такая кнопка читается как поломка
 * системы, а не как «вам сюда нельзя».
 */
export const useCanEditDictionary = (): boolean => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  return roles.some((role) =>
    [UserRole.ADMIN, UserRole.METHODIST].includes(role),
  );
};
